import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid2,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRequisitionFormSchema, RequisitionFormType } from "../../../types/forms/warehouse/requisition";
import { EquipmentResponse } from "../../../types/model/warehouse/equipment";
import { RequisitionResponse } from "../../../types/model/warehouse/requisition";
import { formatDMY, toDateInput } from "../../../utils/date";

interface RequisitionFormProps {
  formId: string;
  requisitionData?: RequisitionResponse;
  equipmentOptions?: EquipmentResponse[];
  isLoadingEquipment?: boolean;
  isSaving?: boolean;
  onSubmit?: (values: RequisitionFormType) => Promise<void>;
}

// Order and column size of each guide field
const GUIDE_LAYOUT = {
  project: { xs: 12, md: 8 },
  fiscalNumber: { xs: 12, md: 4 },
  origin: { xs: 12, md: 6 },
  destination: { xs: 12, md: 6 },
  vehicle: { xs: 12, md: 4 },
  licensePlate: { xs: 6, md: 4 },
  departureTime: { xs: 6, md: 4 },
} as const;

type GuideField = keyof typeof GUIDE_LAYOUT;
const GUIDE_FIELDS = Object.keys(GUIDE_LAYOUT) as GuideField[];

interface FormValues {
  equipmentIds: number[];
  startDate: string;
  endDate: string;
  autoReturn: boolean;
  notes: string;
  withTransportGuide: boolean;
  transportGuide: Record<GuideField, string>;
}

interface PickerFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
}

// Date as DD/MM/YYYY, state keeps YYYY-MM-DD
const DateField = ({ label, value, onChange, error, disabled, required, size }: PickerFieldProps) => (
  <DatePicker
    label={label}
    format="DD/MM/YYYY"
    value={value ? moment(value, "YYYY-MM-DD") : null}
    onChange={(d) => onChange(d?.isValid() ? d.format("YYYY-MM-DD") : "")}
    disabled={disabled}
    slotProps={{ textField: { fullWidth: true, required, size, error: !!error, helperText: error } }}
  />
);

// Time in 24h, state keeps HH:mm
const TimeField = ({ label, value, onChange, error, disabled, required, size }: PickerFieldProps) => (
  <TimePicker
    label={label}
    ampm={false}
    format="HH:mm"
    value={value ? moment(value, "HH:mm") : null}
    onChange={(d) => onChange(d?.isValid() ? d.format("HH:mm") : "")}
    disabled={disabled}
    slotProps={{ textField: { fullWidth: true, required, size, error: !!error, helperText: error } }}
  />
);

export const equipmentLabel = (e: {
  equipmentType?: string | null;
  equipmentModel?: string | null;
  serialNumber?: string | null;
}) =>
  [[e.equipmentType, e.equipmentModel].filter(Boolean).join(" "), e.serialNumber && `(${e.serialNumber})`]
    .filter(Boolean)
    .join(" ");

export default function RequisitionForm({
  formId,
  requisitionData,
  equipmentOptions = [],
  isLoadingEquipment = false,
  isSaving = false,
  onSubmit,
}: Readonly<RequisitionFormProps>) {
  const { t } = useTranslation();
  const readOnly = !!requisitionData;
  const schema = useMemo(() => getRequisitionFormSchema(t), [t]);

  const guide = requisitionData?.transportGuide;
  const [values, setValues] = useState<FormValues>({
    equipmentIds: requisitionData?.equipment.map((e) => e.equipmentId) ?? [],
    startDate: toDateInput(requisitionData?.startDate),
    endDate: toDateInput(requisitionData?.endDate),
    autoReturn: requisitionData?.autoReturn ?? false,
    notes: requisitionData?.notes ?? "",
    withTransportGuide: !!guide,
    transportGuide: {
      project: guide?.project ?? "",
      origin: guide?.origin ?? "",
      destination: guide?.destination ?? "",
      departureTime: guide?.departureTime ?? "",
      vehicle: guide?.vehicle ?? "",
      licensePlate: guide?.licensePlate ?? "",
      fiscalNumber: guide?.fiscalNumber ?? "",
    },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const setGuideField = (key: GuideField, value: string) =>
    setValues((prev) => ({ ...prev, transportGuide: { ...prev.transportGuide, [key]: value } }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (readOnly || !onSubmit) return;

    const result = schema.safeParse({
      equipmentIds: values.equipmentIds,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
      autoReturn: values.autoReturn,
      notes: values.notes || null,
      transportGuide: values.withTransportGuide
        ? { ...values.transportGuide, fiscalNumber: values.transportGuide.fiscalNumber || undefined }
        : null,
    });

    if (!result.success) {
      const next: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        // Guide errors keep the nested path (e.g. transportGuide.project)
        const key = issue.path[0] === "transportGuide" ? issue.path.join(".") : String(issue.path[0] ?? "form");
        next[key] ??= issue.message;
      });
      setErrors(next);
      return;
    }

    setErrors({});
    await onSubmit(result.data);
  };

  const selectedEquipment = equipmentOptions.filter((e) => values.equipmentIds.includes(e.id));
  const showGuide = !readOnly || !!guide;

  return (
    <form id={formId} onSubmit={handleSubmit} noValidate>
      <Grid2 container spacing={3}>
        {!readOnly && (
          <Grid2 size={12}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={equipmentOptions}
              value={selectedEquipment}
              loading={isLoadingEquipment}
              getOptionLabel={(option: EquipmentResponse) => equipmentLabel(option)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, options) =>
                setField(
                  "equipmentIds",
                  options.map((o) => o.id),
                )
              }
              disabled={isSaving}
              limitTags={3}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                return (
                  <li key={key} {...rest}>
                    <Checkbox size="small" checked={selected} sx={{ mr: 1, ml: -0.5 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {option.equipmentType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {[option.equipmentModel, option.serialNumber].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("warehouse.requisitions.equipment")}
                  required
                  error={!!errors.equipmentIds}
                  helperText={errors.equipmentIds}
                />
              )}
            />
          </Grid2>
        )}

        <Grid2 size={{ xs: 12, md: 6 }}>
          {readOnly ? (
            <TextField
              label={t("warehouse.requisitions.start-date")}
              value={formatDMY(values.startDate)}
              disabled
              fullWidth
            />
          ) : (
            <DateField
              label={t("warehouse.requisitions.start-date")}
              value={values.startDate}
              onChange={(v) => setField("startDate", v)}
              error={errors.startDate}
              disabled={isSaving}
            />
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          {readOnly ? (
            <TextField
              label={t("warehouse.requisitions.end-date")}
              value={formatDMY(values.endDate)}
              disabled
              fullWidth
            />
          ) : (
            <DateField
              label={t("warehouse.requisitions.end-date")}
              value={values.endDate}
              onChange={(v) => setField("endDate", v)}
              error={errors.endDate}
              disabled={isSaving}
              required={values.autoReturn}
            />
          )}
        </Grid2>

        <Grid2 size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={values.autoReturn}
                onChange={(e) => setField("autoReturn", e.target.checked)}
                disabled={readOnly || isSaving}
              />
            }
            label={t("warehouse.requisitions.auto-return")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t("warehouse.requisitions.auto-return-description")}
          </Typography>
        </Grid2>

        <Grid2 size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={values.autoReturn}
                onChange={(e) => setField("autoReturn", e.target.checked)}
                disabled={readOnly || isSaving}
              />
            }
            label={t("warehouse.requisitions.auto-return")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t("warehouse.requisitions.auto-return-description")}
          </Typography>
        </Grid2>

        <Grid2 size={12}>
          <TextField
            label={t("warehouse.requisitions.notes")}
            value={values.notes}
            onChange={(e) => setField("notes", e.target.value)}
            error={!!errors.notes}
            helperText={errors.notes}
            disabled={readOnly || isSaving}
            multiline
            minRows={2}
            fullWidth
          />
        </Grid2>

        {/* Transport guide */}
        {showGuide && (
          <Grid2 size={12}>
            <Paper
              variant="outlined"
              sx={{
                overflow: "hidden",
                borderColor: values.withTransportGuide ? "primary.main" : "divider",
                transition: "border-color 0.2s",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, bgcolor: "action.hover" }}>
                <LocalShippingOutlinedIcon color={values.withTransportGuide ? "primary" : "action"} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2">{t("warehouse.requisitions.transport-guide.title")}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t("warehouse.requisitions.transport-guide.description")}
                  </Typography>
                </Box>
                {!readOnly && (
                  <Switch
                    checked={values.withTransportGuide}
                    onChange={(e) => setField("withTransportGuide", e.target.checked)}
                    disabled={isSaving}
                  />
                )}
              </Box>

              <Collapse in={values.withTransportGuide} unmountOnExit>
                <Box sx={{ p: 2 }}>
                  {errors.transportGuide && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errors.transportGuide}
                    </Alert>
                  )}
                  <Grid2 container spacing={2}>
                    {GUIDE_FIELDS.map((field) => (
                      <Grid2 key={field} size={GUIDE_LAYOUT[field]}>
                        {field === "departureTime" ? (
                          <TimeField
                            size="small"
                            label={t(`warehouse.requisitions.transport-guide.${field}`)}
                            value={values.transportGuide.departureTime}
                            onChange={(v) => setGuideField("departureTime", v)}
                            error={errors["transportGuide.departureTime"]}
                            disabled={readOnly || isSaving}
                            required={!readOnly}
                          />
                        ) : (
                          <TextField
                            size="small"
                            label={t(`warehouse.requisitions.transport-guide.${field}`)}
                            value={values.transportGuide[field]}
                            onChange={(e) => setGuideField(field, e.target.value)}
                            error={!!errors[`transportGuide.${field}`]}
                            helperText={errors[`transportGuide.${field}`]}
                            required={!readOnly && field !== "fiscalNumber"}
                            disabled={readOnly || isSaving}
                            fullWidth
                          />
                        )}
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
              </Collapse>
            </Paper>
          </Grid2>
        )}

        {readOnly && (
          <>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                label={t("warehouse.requisitions.requested-by")}
                value={requisitionData.requestedByName}
                fullWidth
                disabled
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                label={t("warehouse.requisitions.created-on")}
                value={formatDMY(requisitionData.createdOn)}
                fullWidth
                disabled
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                label={t("warehouse.requisitions.returned-on")}
                value={formatDMY(requisitionData.returnedOn)}
                helperText={
                  requisitionData.returnedAutomatically ? t("warehouse.requisitions.returned-automatically") : undefined
                }
                fullWidth
                disabled
              />
            </Grid2>
          </>
        )}
      </Grid2>
    </form>
  );
}